import React, { useState, useEffect } from 'react';

//MUI
import { Box, Card, Grid, IconButton } from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { Delete, Edit } from '@mui/icons-material';

//COMPONENTS
import CustomTable from '@components/Tables/CustomTable';
import FormTemplateQR from '@components/Events/FormTemplateQR';

//SERVICES
import events from '@services/EventsServices';

//MIDDLEWARE
import middleware from '@middlewares/middleware';

//UTILS
import { Swal, SwalDelete } from '@utils/alerts';
import { isNullOrUndefined } from '@utils/validations';
import { convertBase64 } from '@utils/Utilities';

const TableTemplate = (props) => {
  const { idTipo, setIsLoading, refresh } = props;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [page] = useState(0);
  const [pageSize] = useState(25);
  const [openModal, setOpenModal] = useState(false);
  const [params, setParams] = useState({});
  const hasEditPermission = middleware.checkMenuAction('Editar');
  const hasDeletePermission = middleware.checkMenuAction('Eliminar');

  const baseColumns = [
    { id: 'Titulo', label: 'Titulo', align: 'left' },
    { id: 'image', label: 'Imagen', align: 'left', sort: false },
    ...(hasDeletePermission || hasEditPermission ? [{ id: 'Accion', label: '', align: 'center' }] : []),
  ];

  const WhiteTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#fff',
      color: '#000',
      boxShadow: '0px 0px 10px rgba(0,0,0,0.1)',
      fontSize: 14,
    },
  });

  const mapData = (data) =>
    data.map((item, index) => ({
      ...(hasDeletePermission || hasEditPermission
        ? {
            Accion: (
              <>
                {![1, 2, 3].includes(item.id) && hasDeletePermission && (
                  <Tooltip title="Eliminar" placement="right-start">
                    <IconButton onClick={() => deleteConfirmation(item)} sx={{ m: 0, p: 0 }} color="error">
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
                {hasEditPermission && (
                  <Tooltip title="Editar" placement="right-start">
                    <IconButton onClick={() => handleEdit(item)} sx={{ m: 0, p: 0 }} color="primaryDark">
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ),
          }
        : {}),
      image: !isNullOrUndefined(item.RutaImagen) && (
        <WhiteTooltip
          title={<img src={item.RutaImagen} alt={item.imageName} style={{ maxWidth: 200, maxHeight: 200 }} />}
          placement="right"
        >
          <a href={item.RutaImagen} target="_blank" rel="noopener noreferrer">
            {item.NombreImagen || item.Titulo}
          </a>
        </WhiteTooltip>
      ),
      ...item,
    }));

  const handleDelete = async ({ id }) => {
    try {
      setIsLoading(true);

      const result = await events.deleteTemplate({ id });
      const { results, message } = result;

      if (results) {
        Swal.fire({ title: message, icon: 'success', customClass: { container: 'modal-alert' } });
        ApiResponse(params);
      } else throw new Error(message);
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning', customClass: { container: 'modal-alert' } });
    } finally {
      setIsLoading(false);
    }
  };

  const ApiResponse = async () => {
    try {
      setLoading(true);
      const result = await events.getTemplate({
        filtered: [{ id: 'idTipo', value: [0, idTipo], filter: 'IN' }],
      });
      const { results, response, message } = result;
      if (results) {
        setData(mapData(response.data));
      } else {
        throw new Error(message);
      }
    } catch (error) {
      Swal.fire({
        title: error.message,
        icon: 'warning',
        customClass: {
          container: 'modal-alert',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteConfirmation = async ({ Titulo, id, NombreImagen }) => {
    SwalDelete({
      title: 'Eliminar Plantilla',
      text: `¿Estas seguro de querer eliminar: ${Titulo}?`,
      buttonText: 'Eliminar Platilla',
      onDelete: () => handleDelete({ id, NombreImagen }),
    });
  };

  const handleEdit = (value) => {
    setParams(value);
    setOpenModal(true);
  };

  const handleUpdate = async (value) => {
    try {
      setIsLoading(true);

      const file = value.files[0]?.file;

      if (file) {
        if (typeof file === 'string' && file.startsWith('http')) {
          delete value.files;
        } else if (file instanceof File) {
          const base64File = await convertBase64(file);
          value.files[0].file = base64File;
        }
      }

      const result = await events.updateTemplate(value);
      const { results, message } = result;

      if (results) {
        Swal.fire({
          title: message,
          icon: 'success',
          customClass: {
            container: 'modal-alert',
          },
        });
        setOpenModal(false);
      } else {
        throw new Error(message);
      }
    } catch (error) {
      Swal.fire({
        title: error.message,
        icon: 'warning',
        customClass: {
          container: 'modal-alert',
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    ApiResponse();
    //eslint-disable-next-line
  }, [refresh]);

  return (
    <Box component={Card} variant="outlined" borderRadius={2} padding={2} minHeight={100} elevation={0}>
      <Grid container>
        <Grid item xs={12}>
          <CustomTable
            rows={data}
            columns={baseColumns}
            total={data.length}
            page={page}
            pageSize={pageSize}
            stickyHeader={true}
            isLoading={loading}
            maxHeight={600}
            disableCardType
          />
        </Grid>
      </Grid>
      {openModal && (
        <FormTemplateQR
          setModalOpen={setOpenModal}
          modalOpen={openModal}
          params={params}
          idTipo={idTipo}
          setData={handleUpdate}
        />
      )}
    </Box>
  );
};

export default TableTemplate;
